import { useCallback, useMemo, useRef } from "react";
import {
  CopySelectionRange,
  CopySelectionStore,
} from "../context/CopySelectionContext";

export const useCreateCopySelectionStore = (): CopySelectionStore => {
  const rangeRef = useRef<CopySelectionRange | null>(null);
  const listenersRef = useRef<Set<() => void>>(new Set());

  const emitChange = useCallback(() => {
    listenersRef.current.forEach((listener) => listener());
  }, []);

  const store = useMemo<CopySelectionStore>(() => {
    const getRange = (): CopySelectionRange | null => rangeRef.current;

    const setRange = (range: CopySelectionRange | null) => {
      rangeRef.current = range;
      emitChange();
    };

    const clear = () => {
      if (rangeRef.current !== null) {
        rangeRef.current = null;
        emitChange();
      }
    };

    const isCellSelected = (rowIndex: number, columnId: string): boolean => {
      const range = rangeRef.current;
      if (!range) return false;
      return (
        rowIndex >= range.minRow &&
        rowIndex <= range.maxRow &&
        range.selectedColumnIds.has(columnId)
      );
    };

    const hasSelection = (): boolean => rangeRef.current !== null;

    const subscribe = (listener: () => void) => {
      listenersRef.current.add(listener);
      return () => {
        listenersRef.current.delete(listener);
      };
    };

    return {
      getRange,
      setRange,
      clear,
      isCellSelected,
      hasSelection,
      subscribe,
    };
  }, [emitChange]);

  return store;
};
